"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { BookOpen, ChevronDown, Globe, LayoutGrid, Menu, Search, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import { localeNames, locales, type Locale } from "@/lib/i18n";
import { LocalePill, useSwitchLocale } from "@/components/LanguageSwitcher";
import { useAuth } from "@/components/providers/auth-provider";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { IntentLink } from "./site-nav/IntentLink";
import { AccountMenu } from "./site-nav/AccountMenu";
import { MorePanel, useEntryText } from "./site-nav/MorePanel";
import { PhoneMenu } from "./site-nav/PhoneMenu";
import type { SearchPage } from "./site-nav/SiteSearch";
import { loadNextCohort } from "./site-nav/next-cohort";
import {
  isEntryActive,
  isMoreActive,
  moreGroups,
  primaryEntries,
  startFreeHref,
  type NavEntry,
} from "./site-nav/nav-model";
import styles from "./site-nav/site-header.module.css";

// The search is code nobody runs until they open it, so it is its own chunk.
const loadSiteSearch = () => import("./site-nav/SiteSearch");
const SiteSearch = dynamic(loadSiteSearch, { ssr: false });

type Props = {
  locale: Locale;
  siteName?: string;
  /** Locales the visitor may select in the nav (from admin Site Settings). Omitted = all app locales. */
  visibleLocales?: Locale[];
};

function effectiveVisibleLocales(visibleLocales: Locale[] | undefined): Locale[] {
  if (visibleLocales && visibleLocales.length > 0) {
    return locales.filter((l) => visibleLocales.includes(l));
  }
  return [...locales];
}

/** `/` opens the search from anywhere, as on most developer sites, unless the visitor is typing. */
function typingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 160;

const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

/**
 * The site bar (`SITE-NAV01`, the 2026-09-24 canvas, D1 and D2).
 *
 * Light, the original orange logo, and the six places with their icons, the
 * current one in a peach pill, as the Judge bar does on its dark ground. From
 * 1366 px the links carry icons; from 1440 the Learn link's Free chip and the
 * language pill show (`site-header.module.css`); below 1024 the phone menu
 * takes over. More opens the whole site on one panel with the next live
 * cohort. Signed out the one orange button is "Start free" (Learn C) with Log
 * in beside it; signed in it is "My learning" with the account menu.
 *
 * Every link fetches its page on intent, not on sight (`IntentLink`), and the
 * bar's edges sit on the page container's, not on a narrower one of its own.
 */
export function Header({ locale, siteName = "Progsity", visibleLocales }: Props) {
  const languageOptions = effectiveVisibleLocales(visibleLocales);
  const pathname = usePathname();
  const t = useTranslations("nav");
  const text = useEntryText();
  const { user, isAuthenticated, isLoading } = useAuth();
  const switchLocale = useSwitchLocale(locale);
  // `RV-20`: Log in comes back to this page.
  const signInHref = useSignInHref(locale);

  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [phoneTop, setPhoneTop] = useState<number | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const phoneButtonRef = useRef<HTMLButtonElement>(null);
  const timers = useRef<{ open?: ReturnType<typeof setTimeout>; close?: ReturnType<typeof setTimeout> }>({});
  // Opened by a click or a key, the panel stays until dismissed; opened by
  // hovering, it closes when the pointer leaves.
  const morePinned = useRef(false);
  const morePanelId = useId();
  const phoneMenuId = useId();
  const phoneOpen = phoneTop !== null;

  const base = `/${locale}`;
  const signedIn = isAuthenticated && !!user;
  // A teacher who is not signed in lands on the Quiz & Exams pitch, which
  // carries its own sign-in path; the nav sending them straight to a login
  // wall was the first thing the 2026-09-03 review's visitor walked into.
  const createQuizHref = signedIn ? `${base}/dashboard/author-studio/quizzes/new` : `${base}/quiz-exams`;
  const primary = useMemo(() => primaryEntries(locale), [locale]);
  const groups = useMemo(() => moreGroups(locale, createQuizHref), [locale, createQuizHref]);
  const isActive = useCallback((entry: NavEntry) => isEntryActive(entry, pathname), [pathname]);
  const moreActive = isMoreActive(groups, primary, pathname);
  // The language control shows only when admin's Site Settings offer more than
  // one language, as before; offered one, a Bangla page shows none rather than
  // a lone "EN".
  const otherLocale = languageOptions.length > 1 ? (languageOptions.find((l) => l !== locale) ?? null) : null;

  const clearTimers = useCallback(() => {
    clearTimeout(timers.current.open);
    clearTimeout(timers.current.close);
  }, []);

  const closeAll = useCallback(() => {
    clearTimers();
    morePinned.current = false;
    setMoreOpen(false);
    setPhoneTop(null);
  }, [clearTimers]);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 4);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    closeAll();
    setSearchOpen(false);
  }, [pathname, closeAll]);

  useEffect(() => clearTimers, [clearTimers]);

  // `/` and Ctrl K (Cmd K) open the search from anywhere on the site's pages.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const slash = event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey && !typingInto(event.target);
      const commandK = event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey);
      if (!slash && !commandK) return;
      event.preventDefault();
      closeAll();
      setSearchOpen(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeAll]);

  // The More panel and the phone menu close on Escape (focus back on their
  // button) and on a press anywhere outside the bar.
  useEffect(() => {
    if (!moreOpen && !phoneOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      const button = moreOpen ? moreButtonRef.current : phoneButtonRef.current;
      closeAll();
      button?.focus();
    }
    function onPointerDown(event: PointerEvent) {
      if (!headerRef.current?.contains(event.target as Node)) closeAll();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [moreOpen, phoneOpen, closeAll]);

  /** Reaching for More starts what the panel will need: its cohort card reads the course record. */
  function warmMore() {
    void loadNextCohort();
  }

  function hoverOpen(event: React.PointerEvent) {
    if (event.pointerType !== "mouse") return;
    warmMore();
    clearTimers();
    timers.current.open = setTimeout(() => setMoreOpen(true), OPEN_DELAY_MS);
  }

  function hoverClose(event: React.PointerEvent) {
    if (event.pointerType !== "mouse" || morePinned.current) return;
    clearTimers();
    timers.current.close = setTimeout(() => setMoreOpen(false), CLOSE_DELAY_MS);
  }

  function togglePhone() {
    if (phoneOpen) {
      setPhoneTop(null);
      return;
    }
    setPhoneTop(Math.max(0, headerRef.current?.getBoundingClientRect().bottom ?? 56));
  }

  function openSearch() {
    closeAll();
    setSearchOpen(true);
  }

  const searchPages = useMemo<SearchPage[]>(() => {
    const seen = new Set<string>();
    const out: SearchPage[] = [];
    for (const group of groups) {
      for (const entry of group.entries) {
        if (seen.has(entry.href)) continue;
        seen.add(entry.href);
        out.push({ id: entry.id, label: text(entry).label, href: entry.href, icon: entry.icon });
      }
    }
    return out;
  }, [groups, text]);

  const searchStart = useMemo<SearchPage[]>(
    () => [
      { id: "learn-c", label: t("startFreeWithLearnC"), href: startFreeHref(locale), icon: BookOpen },
      ...searchPages.filter((page) => ["courses", "playground", "judge", "contests"].includes(page.id)),
    ],
    [locale, searchPages, t],
  );

  const searchButton = (className: string) => (
    <Button
      type="button"
      variant="outline"
      aria-label={t("search.label")}
      aria-keyshortcuts="/ Control+K Meta+K"
      onClick={openSearch}
      onPointerEnter={() => void loadSiteSearch()}
      onFocus={() => void loadSiteSearch()}
      className={cn("p-0", className)}
    >
      <Search className="h-[18px] w-[18px]" aria-hidden />
    </Button>
  );

  const startFree = (
    <Button asChild className="h-10 px-4 font-semibold">
      <IntentLink href={startFreeHref(locale)}>
        <BookOpen className="hidden h-4 w-4 xl:block" aria-hidden />
        {t("startFree")}
      </IntentLink>
    </Button>
  );

  const logIn = (
    <Button asChild variant="tertiary" className="h-10 px-2.5 font-semibold xl:px-3">
      <IntentLink href={signInHref}>{t("login")}</IntentLink>
    </Button>
  );

  const moreFooter = (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-neutral-100 bg-neutral-50 px-5 py-3.5 text-[13px] text-neutral-500 xl:px-8">
      <span>
        {t.rich("searchHint", {
          kbd: (chunks) => (
            <kbd className="rounded-md border border-b-2 border-neutral-200 bg-white px-1.5 py-px font-mono text-xs text-neutral-600">
              {chunks}
            </kbd>
          ),
        })}
      </span>
      {signedIn ? null : (
        <span>
          {t("newHere")}{" "}
          <IntentLink
            href={startFreeHref(locale)}
            onClick={closeAll}
            className="rounded font-semibold text-primary-ink underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t("startFreeWithLearnC")}
          </IntentLink>
        </span>
      )}
    </div>
  );

  return (
    <header
      ref={headerRef}
      className={cn(
        "sticky top-0 z-50 border-b bg-white transition-[box-shadow,border-color] duration-200 ease-out",
        scrolled || moreOpen || phoneOpen ? "border-neutral-200" : "border-transparent",
        scrolled && !phoneOpen ? "shadow-card" : null,
      )}
    >
      <Container className="flex h-14 items-center gap-2 lg:h-16">
        <IntentLink
          href={base}
          aria-label={siteName}
          className="flex shrink-0 items-center rounded outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Image src="/logo.svg" alt={siteName} width={138} height={34} priority className="h-7 w-auto xl:h-8" />
        </IntentLink>

        <span className={cn("mx-2 hidden h-6 w-px shrink-0 bg-neutral-200", styles.divider)} aria-hidden />

        <nav aria-label={t("primaryLabel")} className="ml-1 hidden min-w-0 items-center gap-0.5 lg:flex xl:ml-3">
          {primary.map((entry) => {
            const Icon = entry.icon;
            const active = isActive(entry);
            return (
              <IntentLink
                key={entry.id}
                href={entry.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[10px] px-1.5 text-[13px] font-medium transition-colors duration-150 ease-out xl:px-2.5 xl:text-sm",
                  active ? "bg-primary/10 text-primary-ink" : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900",
                  focusRing,
                )}
              >
                <Icon
                  className={cn(
                    "hidden h-[18px] w-[18px] shrink-0 transition-[color,transform] duration-150 ease-out group-hover:-translate-y-px motion-reduce:transform-none",
                    active ? "text-primary" : "text-neutral-400 group-hover:text-primary",
                    styles.navIcon,
                  )}
                  aria-hidden
                />
                {t(entry.label)}
                {entry.key === "learn" ? (
                  <span
                    className={cn(
                      "hidden h-[18px] items-center rounded-full bg-emerald-50 px-1.5 text-[11px] font-bold text-emerald-700",
                      styles.freeChip,
                    )}
                  >
                    {t("free")}
                  </span>
                ) : null}
              </IntentLink>
            );
          })}

          {/* The panel follows its button in the DOM, so Tab walks from More into
              it; it is positioned against the header, the nearest positioned box.
              Focus moving on to something outside both closes it (a press on
              the panel's own background moves focus nowhere and keeps it). */}
          <div
            className="contents"
            onBlur={(event) => {
              const next = event.relatedTarget as Node | null;
              if (moreOpen && next && !event.currentTarget.contains(next)) closeAll();
            }}
          >
            <button
              ref={moreButtonRef}
              type="button"
              aria-expanded={moreOpen}
              aria-controls={morePanelId}
              onClick={() => {
                clearTimers();
                warmMore();
                morePinned.current = !moreOpen;
                setMoreOpen((open) => !open);
              }}
              onFocus={warmMore}
              onPointerEnter={hoverOpen}
              onPointerLeave={hoverClose}
              className={cn(
                "group inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[10px] px-1.5 text-[13px] font-medium transition-colors duration-150 ease-out xl:px-2.5 xl:text-sm",
                moreOpen
                  ? "bg-neutral-100 text-neutral-900"
                  : moreActive
                    ? "bg-primary/10 text-primary-ink"
                    : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900",
                focusRing,
              )}
            >
              <LayoutGrid
                className={cn(
                  "hidden h-[18px] w-[18px] shrink-0",
                  moreActive || moreOpen ? "text-primary" : "text-neutral-400 group-hover:text-primary",
                  styles.navIcon,
                )}
                aria-hidden
              />
              {t("moreMenu")}
              <ChevronDown
                className={cn("h-3.5 w-3.5 shrink-0 transition-transform duration-200 ease-out", moreOpen && "rotate-180")}
                aria-hidden
              />
            </button>
            {moreOpen ? (
              <MorePanel
                id={morePanelId}
                groups={groups}
                isActive={isActive}
                signedIn={signedIn}
                onNavigate={closeAll}
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") clearTimers();
                }}
                onPointerLeave={hoverClose}
                footer={moreFooter}
              />
            ) : null}
          </div>
        </nav>

        <div className="flex-1" />

        {/* From 1024: search, the language, the account. */}
        <div className="hidden shrink-0 items-center gap-1.5 lg:flex xl:gap-2">
          {searchButton("h-10 w-10")}

          {otherLocale ? (
            <>
              <Button
                type="button"
                variant="outline"
                aria-label={t("switchLanguage", { language: localeNames[otherLocale] })}
                title={localeNames[otherLocale]}
                onClick={() => switchLocale(otherLocale)}
                className={cn("h-10 w-10 p-0", styles.langGlobe)}
              >
                <Globe className="h-[18px] w-[18px]" aria-hidden />
              </Button>
              <div className={cn("hidden", styles.langPill)}>
                <LocalePill locale={locale} visibleLocales={languageOptions} />
              </div>
            </>
          ) : null}

          {isLoading ? (
            <>
              {/* Until the session check answers, the signed-out pair holds the
                  place: it is what most visitors get, and "Start free" is in the
                  first paint. A browser that was signed in last time shows the
                  signed-in shape instead (`data-session`, set in the head). */}
              <div className={cn("flex items-center gap-1", styles.guestPending)}>
                {logIn}
                {startFree}
              </div>
              <div className={cn("items-center gap-3", styles.memberPending)} aria-hidden>
                <span className="h-10 w-32 animate-pulse rounded-[10px] bg-neutral-100" />
                <span className="h-9 w-9 animate-pulse rounded-full bg-neutral-100" />
              </div>
            </>
          ) : signedIn ? (
            <div className="flex items-center gap-3">
              <Button asChild className="h-10 px-4 font-semibold">
                <IntentLink href={`${base}/dashboard`}>{t("myLearning")}</IntentLink>
              </Button>
              <AccountMenu locale={locale} user={user!} />
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {logIn}
              {startFree}
            </div>
          )}
        </div>

        {/* Below 1024: search and the menu, 44 px each. */}
        <div className="flex shrink-0 items-center gap-2 lg:hidden">
          {searchButton("h-11 w-11 rounded-xl")}
          <Button
            ref={phoneButtonRef}
            type="button"
            variant="outline"
            aria-label={phoneOpen ? t("menuClose") : t("menuOpen")}
            aria-expanded={phoneOpen}
            aria-controls={phoneMenuId}
            onClick={togglePhone}
            className={cn(
              "h-11 w-11 rounded-xl p-0",
              phoneOpen && "border-neutral-900 bg-neutral-900 text-white hover:border-neutral-900 hover:text-white",
            )}
          >
            {phoneOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </Button>
        </div>
      </Container>

      {phoneTop !== null ? (
        <PhoneMenu
          id={phoneMenuId}
          locale={locale}
          top={phoneTop}
          primary={primary}
          groups={groups}
          isActive={isActive}
          visibleLocales={languageOptions}
          onNavigate={closeAll}
        />
      ) : null}

      {searchOpen ? (
        <SiteSearch
          locale={locale}
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          pages={searchPages}
          start={searchStart}
        />
      ) : null}
    </header>
  );
}
